import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { login } from './authActions'
import {
  Box, TextField, Button, Typography,
  CircularProgress, Alert, CssBaseline
} from '@mui/material'
import { Link } from 'react-router-dom'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import logo from '../../assets/logo.png'
import { IconButton, useTheme } from '@mui/material'

const Login = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { loading, error, isAuthenticated } = useSelector(state => state.auth || {})
  const theme = useTheme()
  const [form, setForm] = useState({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)

  const from = location.state?.from?.pathname || '/'

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, from])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { username, password } = form
    await dispatch(login(username, password))
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      pt: 'env(safe-area-inset-top, 0px)',
      pb: 'env(safe-area-inset-bottom, 0px)',
    }}>
      <CssBaseline />
      {/* Decorative gradient blobs */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        right: -80,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,56,92,0.14) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -60,
        left: -60,
        width: 240,
        height: 240,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,201,177,0.10) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 5,
        px: 3,
        maxWidth: 400,
        width: '100%',
        mx: 'auto',
        position: 'relative',
        zIndex: 1,
      }}>
        <Box
          component="img"
          src={logo}
          alt="SwipeeIT"
          sx={{
            width: 156,
            height: 'auto',
            mb: 5,
            mx: 'auto',
            display: 'block',
            objectFit: 'contain',
            filter: theme.palette.mode === 'dark' ? 'invert(1) brightness(0) invert(1)' : 'none',
          }}
        />

        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Welcome back
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
          Sign in to find your next home
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={form.username}
            onChange={handleChange}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={form.password}
            onChange={handleChange}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(v => !v)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2.5,
              py: 1.75,
              fontSize: '1rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
              boxShadow: '0 4px 16px rgba(255,56,92,0.32)',
              '&:hover': {
                background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)',
                boxShadow: '0 6px 20px rgba(255,56,92,0.44)',
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                to="/register"
                style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 600 }}
              >
                Sign Up
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Login
